import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Spinner from "../../../../../components/Spinner";
import { getRecalledItemsList } from "../../../../../config/Services";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import "./RecalledItemsModal.css";
import { getStoreCurrencySymbol } from "../../../../../utils/currency";

const RecalledItemOrderModal = ({ open, setOpenRecalledItems }) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  const [pageState, setPageState] = useState({
    loader: false,
    dataRows: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const fetchRecalledItemsList = async (page, pageSize, queryVal = debouncedSearch) => {
    setPageState((prev) => ({ ...prev, loader: true }));
    setLoading(true);

    const payload = {
      page,
      pageSize: String(pageSize),
      taskStatuses: ["REMOVED"],
      sortByUpdatedDate: "DESCENDING",
      sortByCreatedDate: "DESCENDING",
      ...(queryVal?.trim() && { query: String(queryVal).trim() }),
    };

    try {
      const res = await getRecalledItemsList(payload);
      const data = res?.data;

      if (!data || !Array.isArray(data?.results) || data?.results?.length === 0) {
        setPageState((prev) => ({
          ...prev,
          loader: false,
          dataRows: [],
          total: data?.totalResults || 0,
          page: data?.currentPage || page,
        }));
        return;
      }

      const mappedRows = data?.results?.map((item, index) => {
        return {
          id: (page - 1) * pageSize + index + 1,
          cartProductId: item.taskId ?? null,
          itemName: item.title ?? "-",
          quantity: item?.quantity ?? 0,
          reason:
            item?.metadata?.recallDescription ||
            item.metadata?.recallReason ||
            item.metadata?.comments ||
            "-",
          table: item.metadata?.tableNumber || "-",
          wastageValue:
            item?.quantity && item?.metadata?.sellingPrice
              ? `${getStoreCurrencySymbol()}${(item.quantity * item.metadata.sellingPrice).toFixed(2)}`
              : "-",
          revenueLoss: item?.metadata?.revenueLoss ? `${getStoreCurrencySymbol()}${item.metadata.revenueLoss}` : "-",
        };
      });

      setPageState((prev) => ({
        ...prev,
        loader: false,
        dataRows: mappedRows,
        total: data.totalResults ?? mappedRows.length,
        page: data.currentPage ?? page,
      }));
    } catch (error) {
      setPageState((prev) => ({
        ...prev,
        loader: false,
        dataRows: [],
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPageState((prev) => ({ ...prev, page: 1 }));
    }, 500); // 500ms debounce delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset search when modal is closed
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setDebouncedSearch("");
      setPageState((prev) => ({ ...prev, page: 1 }));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchRecalledItemsList(pageState?.page, pageState?.pageSize, debouncedSearch);
    }
  }, [open, pageState?.page, pageState?.pageSize, debouncedSearch]);

  const handlePrevPage = () => {
    if (pageState?.page > 1) {
      setPageState((prev) => ({ ...prev, page: prev?.page - 1 }));
    }
  };

  const handleNextPage = () => {
    if (pageState?.page < Math.ceil(pageState?.total / pageState?.pageSize)) {
      setPageState((prev) => ({ ...prev, page: prev?.page + 1 }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Modal open={open} onClose={() => setOpenRecalledItems(false)} className="recalled-items-modal">
      <div className="recalled-items-container">
        <div className="recalled-items-header">
          <div>
            <h2>Recalled Items</h2>
          </div>
          <button className="recalled-items-close-btn" onClick={() => setOpenRecalledItems(false)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="recalled-search-container">
          <div className="recalled-input-container">
            <PosCustomIcon
              icon={<MagnifyingGlassIcon />}
              color={"#535861"}
              height={"1rem"}
              width={"1rem"}
            />
            <input
              type="text"
              placeholder="Search items"
              className="recalled-search-bar"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <div
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                }}
                onClick={handleClearSearch}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <PosCustomIcon
                  icon={<XMarkIcon />}
                  color={"#6B7280"}
                  height={"16px"}
                  width={"16px"}
                />
              </div>
            )}
          </div>
        </div>

        <div className="recalled-items-content">
          <div className="recalled-items-table-wrapper">
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                <Spinner />
              </div>
            ) : pageState?.dataRows?.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                {debouncedSearch?.trim()
                  ? "No recalled items found matching your search."
                  : "No recalled items found."}
              </div>
            ) : (
              <table className="recalled-items-table">
                <thead>
                  <tr>
                    <th className="col-sno">S.no</th>
                    <th className="col-name">Item Name</th>
                    <th className="col-qty text-center">Qty</th>
                    <th className="col-reason">Reason</th>
                    <th className="col-table text-center">Table</th>
                    <th className="col-wastage text-center">Wastage Value</th>
                    <th className="col-revenue text-center">Revenue Loss</th>
                  </tr>
                </thead>
                <tbody className="recalled-items-grid-body">
                  {pageState?.dataRows?.map((row) => (
                    <tr key={row?.id}>
                      <td className="col-sno">
                        <span className="recalled-items-sno">{row?.id}</span>
                      </td>
                      <td className="col-name">
                        <span className="recalled-items-name">{row?.itemName}</span>
                      </td>
                      <td className="col-qty text-center">
                        <span className="recalled-items-qty">{row?.quantity}</span>
                      </td>
                      <td className="col-reason">
                        <span className="recalled-items-reason">{row?.reason}</span>
                      </td>
                      <td className="col-table text-center">
                        <span className="recalled-items-table-num">{row?.table}</span>
                      </td>
                      <td className="col-wastage text-center">
                        <span className="font-semibold text-slate-800">{row?.wastageValue}</span>
                      </td>
                      <td className="col-revenue text-center">
                        <span className="font-semibold text-slate-800">
                          {row?.revenueLoss || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {pageState?.total > pageState?.pageSize && (
          <div className="recalled-items-pagination">
            <div className="pagination-info">
              Showing {(pageState?.page - 1) * pageState?.pageSize + 1} to{" "}
              {Math.min(pageState?.page * pageState?.pageSize, pageState?.total)} of{" "}
              {pageState?.total} items
            </div>
            <div className="pagination-controls-kds-recalled">
              <button
                className="pagination-btn"
                onClick={handlePrevPage}
                disabled={pageState?.page === 1}
              >
                Previous
              </button>
              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={pageState?.page >= Math.ceil(pageState?.total / pageState?.pageSize)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RecalledItemOrderModal;
